import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero, Reveal } from "../components/site/Motion";
import { Steps } from "../components/home/Steps";
import { SourcingForm } from "../components/site/SourcingForm";
import { useSettings } from "../lib/settings";
import { telHref } from "../lib/api";

export function HowItWorks() {
  return (
    <div data-testid="how-it-works-page">
      <PageHero eyebrow="How It Works" title={["A clear process,", "start to finish."]} intro="Whether you are browsing listed inventory or need us to search on your behalf, here is what to expect." testid="how-hero" />
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20"><Steps /></section>
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-24 grid md:grid-cols-2 gap-6">
        {[
          ["Buying listed inventory", "Browse the inventory, review each listing's status and details, then use the inquiry button. We will confirm availability and next steps.", "/inventory", "Browse Inventory"],
          ["Requesting a search", "Submit a sourcing request with whatever details you have. We will search relevant channels and business relationships and keep you informed.", "/sourcing", "Submit a Request"],
        ].map(([t, d, to, cta], i) => (
          <Reveal key={t} delay={i * 0.08} className="border hairline p-8 md:p-10 bg-[var(--ink-2)]">
            <h3 className="font-display text-3xl">{t}</h3>
            <p className="text-slate-300 mt-4 leading-relaxed">{d}</p>
            <Link to={to} className="btn-gold mt-8" data-testid={`how-cta-${i}`}>{cta} <ArrowRight className="w-4 h-4" /></Link>
          </Reveal>
        ))}
      </section>
    </div>
  );
}

export function Sourcing() {
  const [params] = useSearchParams();
  const s = useSettings();
  return (
    <div data-testid="sourcing-page">
      <PageHero eyebrow="Asset Sourcing Requests" title={["Request", "an asset."]} intro="Can't find the right asset in current inventory? Share as much or as little as you know. Every detail beyond your contact information is optional." testid="sourcing-hero" />
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-8"><SourcingForm initialDivision={params.get("division") || ""} initialType={params.get("type") || ""} /></div>
        <aside className="lg:col-span-4 space-y-6">
          <div className="border hairline p-7 bg-[var(--ink-2)]">
            <p className="eyebrow">What happens next</p>
            <ol className="mt-5 space-y-4 text-sm text-slate-300">
              <li><span className="font-mono text-gold mr-2">01</span>We review your requirements.</li>
              <li><span className="font-mono text-gold mr-2">02</span>We search relevant channels and business relationships.</li>
              <li><span className="font-mono text-gold mr-2">03</span>We share options with clear information and keep you informed.</li>
            </ol>
          </div>
          <div className="border hairline p-7">
            <p className="eyebrow">Prefer to talk?</p>
            <a href={telHref(s.phone)} className="block font-display text-3xl mt-4 hover:text-gold transition-colors" data-testid="sourcing-phone-link">{s.phone}</a>
            <a href={`mailto:${s.sales_email}`} className="block text-sm text-slate-300 mt-2 hover:text-gold break-all" data-testid="sourcing-email-link">{s.sales_email}</a>
          </div>
        </aside>
      </section>
    </div>
  );
}
